import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from "../public/eventli-logo.svg";
import { Link } from 'react-router-dom';

const Navbar = () => {
  const [isHovered, setIsHovered] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Disable scrolling when mobile menu is open
  useEffect(() => {
    const preventDefault = (e) => {
      if (mobileMenuOpen) {
        e.preventDefault();
      }
    };

    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.touchAction = 'none';
      document.body.style.width = '100%';
      document.addEventListener('touchmove', preventDefault, { passive: false });
    } else {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.body.style.touchAction = 'auto';
    }
    
    // Cleanup function
    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.body.style.position = 'static';
      document.body.style.touchAction = 'auto';
      document.removeEventListener('touchmove', preventDefault);
    };
  }, [mobileMenuOpen]);

  // Animation variants
  const linkVariants = {
    hover: {
      scale: 1.05,
      color: "#FFD700",
      transition: { duration: 0.2 }
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      backgroundColor: "#FFD700", 
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      transition: { duration: 0.2 }
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 }
    }
  };

  const mobileMenuVariants = {
    hidden: { 
      opacity: 0,
      y: -20,
      transition: {
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const mobileItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  // Hamburger to X animation variants
  const topLine = {
    closed: { rotate: 0, y: 0 },
    open: { rotate: 45, y: 6 }
  };
  
  const middleLine = {
    closed: { opacity: 1 },
    open: { opacity: 0 }
  };
  
  const bottomLine = {
    closed: { rotate: 0, y: 0 },
    open: { rotate: -45, y: -6 }
  };

  return (
    <nav className="flex items-center justify-between px-4 md:px-8 lg:px-[64px] py-[20px] bg-white shadow-sm relative">
      {/* Left side - Logo */}
      <div className='flex items-center justify-between w-full md:w-auto'>
        <motion.div 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="text-[18px] font-normal text-[#FFD700] cursor-pointer"
        >
          <Link to='/'>
            <img src={Logo} alt="Eventli Logo" className="h-8 w-auto" />
          </Link>
        </motion.div>

        {/* Mobile menu button - hidden on desktop */}
        <button 
          className="md:hidden p-2 rounded-md text-gray-700 focus:outline-none"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <motion.div className="w-6 h-6 relative">
            <motion.span
              className="absolute left-0 w-full h-0.5 bg-current"
              variants={topLine}
              animate={mobileMenuOpen ? "open" : "closed"}
              transition={{ duration: 0.2 }}
            />
            <motion.span
              className="absolute left-0 w-full h-0.5 bg-current mt-2"
              variants={middleLine}
              animate={mobileMenuOpen ? "open" : "closed"}
              transition={{ duration: 0.2 }}
            />
            <motion.span
              className="absolute left-0 w-full h-0.5 bg-current mt-4"
              variants={bottomLine}
              animate={mobileMenuOpen ? "open" : "closed"}
              transition={{ duration: 0.2 }}
            />
          </motion.div>
        </button>
      </div>

      {/* Center - Navigation Links (desktop) */}
      <div className="hidden md:flex items-center space-x-4 lg:space-x-8 ml-4 lg:ml-8">
        <Link to='/'>
          <motion.div
            variants={linkVariants}
            whileHover="hover"
            whileTap="tap"
            onHoverStart={() => setIsHovered("create")}
            onHoverEnd={() => setIsHovered(null)}
            className="relative cursor-pointer"
          >
            <span className="text-base lg:text-[18px] font-normal">Home</span>
            {isHovered === "create" && (
              <motion.div 
                layoutId="underline"
                className="absolute bottom-0 left-0 w-full h-0.5 bg-[#FFD700]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                exit={{ scaleX: 0 }}
                transition={{ duration: 0.3 }}
              />
            )}
          </motion.div>
        </Link>

        <Link to='/helpcenter'>
        <motion.div
          variants={linkVariants}
          whileHover="hover"
          whileTap="tap"
          onHoverStart={() => setIsHovered("tickets")}
          onHoverEnd={() => setIsHovered(null)}
          className="relative cursor-pointer"
        >
          <span className="text-base lg:text-[18px]">How it Works</span>
          {isHovered === "tickets" && (
            <motion.div 
              layoutId="underline"
              className="absolute bottom-0 left-0 w-full h-0.5 bg-[#FFD700]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          )}
        </motion.div>
        </Link>

        <Link to='/contact'>
        <motion.div  
          variants={linkVariants}
          whileHover="hover"
          whileTap="tap"
          onHoverStart={() => setIsHovered("help")}
          onHoverEnd={() => setIsHovered(null)}
          className="relative cursor-pointer"
        >
          <span className="text-base lg:text-[18px]">Contact Us</span>
          {isHovered === "help" && (
            <motion.div 
              layoutId="underline"
              className="absolute bottom-0 left-0 w-full h-0.5 bg-[#FFD700]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              transition={{ duration: 0.3 }}
            />
          )}
        </motion.div>
        </Link>
      </div>

      {/* Right side - Auth Buttons (desktop) */}
      <div className="hidden md:flex items-center space-x-3 lg:space-x-4">
        <Link to='/signup'>
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="px-4 lg:px-6 xl:px-10 py-2 lg:py-3 xl:py-4 bg-[#FCD20B] text-sm lg:text-[16px] xl:text-[18px] text-[#1E1E1E] hover:text-[#1E1E1E] rounded-[32px]"
          >
            Sign up
          </motion.button>
        </Link>

        <Link to='/login'>
          <motion.button
            whileHover="hover"
            whileTap="tap"
            className="px-4 lg:px-6 xl:px-10 py-2 lg:py-3 xl:py-4 text-[#FCD20B] bg-[#FFFCEE] text-sm lg:text-[16px] xl:text-[18px] font-medium rounded-[32px]"
          >
            Login
          </motion.button>
        </Link>
      </div>

      {/* Mobile Menu - Fullscreen */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={mobileMenuVariants}
            className="md:hidden fixed inset-0 bg-white z-50 px-4 py-2 mt-[72px] overflow-y-auto"
          >
            <motion.div variants={mobileItemVariants} className="py-4 border-b border-gray-100">
              <Link to='/signup' onClick={() => setMobileMenuOpen(false)}>
                <motion.div
                  whileHover={{ color: "#FFD700" }}
                  className="text-xl font-medium cursor-pointer py-2"
                >
                  Home
                </motion.div>
              </Link>
            </motion.div>
            
            <motion.div variants={mobileItemVariants} className="py-4 border-b border-gray-100">
              <motion.div
                whileHover={{ color: "#FFD700" }}
                className="text-xl font-medium cursor-pointer py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                How it Works
              </motion.div>
            </motion.div>
            
            <motion.div variants={mobileItemVariants} className="py-4 border-b border-gray-100">
              <motion.div
                whileHover={{ color: "#FFD700" }}
                className="text-xl font-medium cursor-pointer py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </motion.div>
            </motion.div>
            
            <motion.div variants={mobileItemVariants} className="py-4 mt-8">
              <Link to='/signup' onClick={() => setMobileMenuOpen(false)}>
                <motion.button
                  whileHover={{ backgroundColor: "#FFD700" }}
                  className="w-full py-4 text-gray-700 bg-[#FCD20B] text-xl rounded-[32px]"
                >
                  Sign up
                </motion.button>
              </Link>
            </motion.div>
            
            <motion.div variants={mobileItemVariants} className="py-4">
              <Link to='/login' onClick={() => setMobileMenuOpen(false)}>
                <motion.button
                  className="w-full py-4 text-[#FCD20B] bg-[#FFFCEE] text-xl rounded-[32px]"
                >
                  Login
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;